import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ButtonMantine, Group, Text, notifications } from 'ui';

import { usePlatformService } from './usePlatformService';

const DEFAULT_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 6 * 1000,
  maximumAge: 120 * 1000,
};
type GetLocation = () => Promise<{
  latitude: number;
  longitude: number;
  timestamp: GeolocationPosition['timestamp'];
}>;

type UseUserLocation = {
  /** getCurrentPosition Options. Changes do not trigger getCurrentPosition   */
  options?: PositionOptions;
  /** Gets the updated location when the users permissions have changed.
   * @default true
   */
  getLocationOnPermissionChange?: boolean;

  /**
   * Shows a prompt notification when the location status == prompt
   * Does not update after the first render
   */
  showPromptNotification?: boolean;

  /**
   * Callback fired when getLocation is successful
   * Not updated after the first render
   */
  getLocationCallback?: (position: Awaited<ReturnType<GetLocation>>) => void;
};

interface UserLocationStateBase {
  loading: boolean;
  timestamp?: GeolocationPosition['timestamp'];
  geoLocation?: Omit<GeolocationCoordinates, 'toJSON'>;
}
interface UserLocationStateError extends Partial<UserLocationStateBase> {
  error: GeolocationPositionError;
}
interface UserLocationStateLoading extends Partial<UserLocationStateBase> {
  loading: true;
}
type UserLocationState = UserLocationStateBase | UserLocationStateLoading | UserLocationStateError;

const useUserLocation = ({
  options = {},
  getLocationOnPermissionChange = true,
  showPromptNotification = false,
  getLocationCallback,
}: UseUserLocation = {}) => {
  const Platform = usePlatformService();

  /** Combines hook options with the default options. Changes do not trigger a new a new geolocation event  */
  const optionsRef = useRef({
    ...DEFAULT_OPTIONS,
    ...options,
  });
  const showPromptRef = useRef(showPromptNotification);

  /** Provides stable reference for getLocation */
  const getLocationCbRef = useRef(getLocationCallback);

  const [permissionStatus, setPermissionStatus] = useState<PermissionState | undefined>();

  const [state, setState] = useState<UserLocationState>({
    loading: true,
  });

  const onEvent = useCallback(({ coords, timestamp }: GeolocationPosition) => {
    setState({
      loading: false,
      timestamp,
      geoLocation: {
        altitude: coords.altitude,
        latitude: coords.latitude,
        longitude: coords.longitude,
        altitudeAccuracy: coords.altitudeAccuracy,
        accuracy: coords.accuracy,
        heading: coords.heading,
        speed: coords.speed,
      },
    });
  }, []);

  const onEventError = useCallback((error: GeolocationPositionError) => {
    setState((prev: UserLocationState) => ({
      ...prev,
      loading: false,
      error,
    }));
  }, []);

  const getLocation: GetLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      // Sets local state then calls the resolves or rejects
      const forwardEvent = (position: GeolocationPosition) => {
        onEvent(position);
        getLocationCbRef.current?.({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          timestamp: position.timestamp,
        });
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          timestamp: position.timestamp,
        });
      };
      const forwardError = (error: GeolocationPositionError) => {
        onEventError(error);
        reject(error);
      };
      if (permissionStatus == 'denied') {
        setPermissionStatus('denied');
        reject({ message: 'Permission Denied' });
      } else {
        navigator.geolocation.getCurrentPosition(forwardEvent, forwardError, optionsRef.current);
      }
    });
  }, [onEvent, onEventError, permissionStatus]);

  const showPermissionPrompt = useCallback(
    () => {
      notifications.show({
        id: 'user-location-permission-prompt',
        title: 'Allow Location Access',
        message: (
          <Group gap="sm" wrap="nowrap" justify="space-between">
            <Text fz="sm" c="gray">
              Givebacks would like to use your location
            </Text>
            <ButtonMantine
              size="sm"
              variant="subtle"
              flex={'0 0 auto'}
              onClick={() => {
                getLocation();
                notifications.hide('user-location-permission-prompt');
              }}
            >
              Allow
            </ButtonMantine>
          </Group>
        ),
        // message: 'Givebacks would like to use your location',
        styles: { closeButton: { alignSelf: 'flex-start' } },
        autoClose: false,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Exclude getLocation from being a required dep here
    []
  );

  useEffect(() => {
    /**
     * Set the permission status and watch for changes
     */
    const setPermissionQuery = () => {
      navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
        // Set the initial state
        setPermissionStatus(permissionStatus.state);
        if (permissionStatus.state === 'prompt' && showPromptRef.current) {
          showPermissionPrompt();
        }

        // Watch for changes
        permissionStatus.onchange = () => {
          setPermissionStatus(permissionStatus.state);
        };
      });
    };

    if (Platform.isNative == false) {
      setPermissionQuery();
    }
  }, [Platform.isNative, showPermissionPrompt]);

  useEffect(() => {
    const handleGetNativeLocation = async () => {
      setPermissionStatus('granted');
      try {
        await getLocation();
      } catch {}
    };
    const handleGetLocation = async () => {
      try {
        await getLocation();
      } catch {}
    };

    /**
     * Native Platforms will handle permissions differently
     * since we get direct access to the device
     * so we can always request the location
     */
    if (Platform.isNative) {
      handleGetNativeLocation();
    }
    // This prevents the user from getting a location request without permission
    if (permissionStatus == 'granted' && getLocationOnPermissionChange) {
      handleGetLocation();
    }
  }, [Platform.isNative, getLocation, getLocationOnPermissionChange, permissionStatus]);

  useEffect(() => {
    // Ensure the notification is closed if the permission is granted
    if (permissionStatus == 'granted') {
      notifications.hide('user-location-permission-prompt');
    }
  }, [permissionStatus]);

  // Watch Location Effect: WIP
  // useEffect(() => {
  //   const watchId = navigator.geolocation.watchPosition(onEvent, onEventError, optionsRef.current);
  //   return () => {
  //     navigator.geolocation.clearWatch(watchId);
  //   };
  // }, [onEvent, onEventError]);

  return {
    locationState: state,
    status: permissionStatus,
    geolocation: state.geoLocation,
    getLocation,
    /**
     * Returns true if the status is not undefined
     * In theory this should be defined when their is a location however there may be
     * race conditions where the location is or is not defined before the status changes: If
     * that is a true case we can also evaluate if we've checked for the location
     */
    isStatusLoading: useMemo(() => permissionStatus !== undefined, [permissionStatus]),
  };
};

export { useUserLocation };
